import {zodResolver} from '@hookform/resolvers/zod';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import {Box, Button, Dialog, DialogActions, DialogContent, IconButton, Stack, Tooltip} from '@mui/material';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFns';
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider';
import type * as React from 'react';
import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {z} from 'zod';
import type {AutocompleteOption} from '@/components/Capacity/CapacityDefaultAutoComplete';
import CapacityDefaultAutoComplete from '@/components/Capacity/CapacityDefaultAutoComplete';
import {useAddCapacityOverride} from '@/hooks/capacity/useAddCapacityOverride';
import type {CapacityLabel} from '@/types/CapacityLabel';
import type {Employee} from '@/types/Employee';
import {errorMap} from '@/utils/zod';

export const allFiltersSchema = z.object({
    project: z.string().optional(),
});

export type AllFiltersForm = z.infer<typeof allFiltersSchema>;

type Props = {
    employee : Employee;
    open : boolean;
    setOpen : (open : boolean) => void;
    capacityLabels : CapacityLabel[];
    defaultCapacityYearMonth : string;
};

const AddEmployeeCapacityOverrideModal : React.FC<Props> = (
    {
        employee,
        open,
        setOpen,
        capacityLabels,
        defaultCapacityYearMonth,
    } : Props
) => {
    const useAddCapacityOverrideMutation = useAddCapacityOverride();
    const [capacityLabel, setCapacityLabel] = useState<AutocompleteOption | undefined>();
    const [textValue, setTextValue] = useState('');

    const form = useForm<AllFiltersForm>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        resolver: zodResolver(allFiltersSchema, {errorMap}),
    });

    const doCreateCapacity = () => {
        setOpen(false);

        if (capacityLabel) {
            useAddCapacityOverrideMutation.mutate({
                associateID: employee.id,
                hours: 0,
                labelText: capacityLabel.label,
                capacityMonth: defaultCapacityYearMonth,
            });
        } else {
            useAddCapacityOverrideMutation.mutate({
                associateID: employee.id,
                hours: 0,
                labelText: textValue,
                capacityMonth: defaultCapacityYearMonth,
            });
        }
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    const handleSubmit = () => {
        setOpen(false);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Tooltip title="Add Capacity For Employee">
                <IconButton size="small" color="inherit" onClick={handleOpen}>
                    <AddCircleIcon/>
                </IconButton>
            </Tooltip>
            <Dialog open={open} fullWidth={true} maxWidth='sm' onClose={handleCloseModal}>
                <form onSubmit={form.handleSubmit(handleSubmit)} noValidate>
                    <DialogContent>
                        <Box
                            sx={{
                                textAlign: 'center',
                                fontWeight: 800,
                            }}
                        >
                            Create Capacity Override
                        </Box>
                        <CapacityDefaultAutoComplete
                            value={capacityLabel}
                            capacityLabels={capacityLabels}
                            setValue={setCapacityLabel}
                            setTextValue={setTextValue}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Stack
                            direction='row'
                            justifyContent='flex-end'
                            sx={{width: '100%'}}
                        >
                            <Button onClick={handleCloseModal}>Cancel</Button>
                            <Button
                                type='submit'
                                onClick={() => {
                                    doCreateCapacity();
                                    setTextValue('');
                                    setCapacityLabel(undefined);
                                }}
                            >
                                Create Capacity Row
                            </Button>
                        </Stack>
                    </DialogActions>
                </form>
            </Dialog>
        </LocalizationProvider>
    );
};

export default AddEmployeeCapacityOverrideModal;
